import {VaultRepository} from './repository';
import type {Profile,Document,FinancialYear,ReferenceBlock,Relationship,ActivityRecord,DashboardSummary,ProfileCard,DocumentFilter,StagedDocument,DuplicateCheckResult,DocumentVersion,SearchResult,User,DocumentType} from '../types/vault';
import {config} from './config';

export class PocketBaseVaultRepository implements VaultRepository {
  public providerName = 'PocketBaseVaultRepository';
  private pbUrl: string;
  private token: string | null = null;
  private currentUser: User | null = null;

  constructor(pbUrl: string) {
    this.pbUrl = pbUrl.replace(/\/$/, '');
    this.loadAuthFromStorage();
  }

  private loadAuthFromStorage() {
    try {
      const stored = localStorage.getItem('mfv_pb_auth');
      if (stored) {
        const p = JSON.parse(stored);
        this.token = p.token;
        this.currentUser = p.model;
      }
    } catch { /* ignore */ }
  }

  private saveAuthToStorage(token: string, model: any) {
    this.token = token;
    this.currentUser = {
      id: model.id,
      email: model.email,
      name: model.name || model.email.split('@')[0],
      role: model.role || 'read_only',
      created: model.created
    };
    localStorage.setItem('mfv_pb_auth', JSON.stringify({ token: this.token, model: this.currentUser }));
  }

  private clearAuth() {
    this.token = null;
    this.currentUser = null;
    localStorage.removeItem('mfv_pb_auth');
  }

  private parsePbError(data: any): string {
    if (!data) return 'Request failed with no response data.';
    let message = data.message || '';
    if (data.data && typeof data.data === 'object') {
      const fieldErrors: string[] = [];
      for (const [field, val] of Object.entries(data.data)) {
        if (val && typeof val === 'object') {
          const v = val as any;
          if (v.message) fieldErrors.push(`${field}: ${v.message}`);
        } else if (typeof val === 'string') {
          fieldErrors.push(`${field}: ${val}`);
        }
      }
      if (fieldErrors.length > 0) return fieldErrors.join('; ');
    }
    return message || 'PocketBase error';
  }

  private async pbFetch(path: string, options: RequestInit = {}): Promise<any> {
    const headers: Record<string, string> = {};
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
    if (options.body && !(options.body instanceof FormData)) headers['Content-Type'] = 'application/json';
    if (options.headers) Object.assign(headers, options.headers as Record<string, string>);
    
    const res = await fetch(`${this.pbUrl}${path}`, ({ ...options, headers } as RequestInit));
    if (res.status === 401) {
      this.clearAuth();
      throw new Error('Unauthorized or session expired');
    }
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      const e = new Error(this.parsePbError(data) || `PocketBase error: ${res.status}`);
      (e as any).status = res.status;
      throw e;
    }
    return data;
  }

  private async pbFetchAll(collection: string, extraQuery: string = ''): Promise<any[]> {
    const perPage = 500;
    let page = 1;
    let allItems: any[] = [];
    while (true) {
      const res = await this.pbFetch(`/api/collections/${collection}/records?perPage=${perPage}&page=${page}${extraQuery}`);
      allItems = allItems.concat(res.items || []);
      if (res.items.length < perPage || res.totalPages <= page) break;
      page++;
    }
    return allItems;
  }

  private normalizeMimeType(file: File): string {
    if (file.type && file.type !== 'application/octet-stream' && file.type !== 'binary/octet-stream') return file.type;
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    return config.upload.mimeMap[ext] || 'application/octet-stream';
  }

  private prepareFileForMultipart(file: File): File {
    const normalizedType = this.normalizeMimeType(file);
    if (file.type === normalizedType) return file;
    return new File([file], file.name, { type: normalizedType, lastModified: file.lastModified });
  }

  private validateFileForUpload(file: File): void {
    if (file.size === 0) throw new Error('This file is empty and cannot be uploaded.');
    if (file.size > config.upload.maxFileSize) throw new Error(`File exceeds the ${config.upload.maxFileSizeLabel} limit.`);
    const ext = '.' + (file.name.split('.').pop()?.toLowerCase() || '');
    if (!config.upload.allowedExtensions.includes(ext)) throw new Error('Unsupported file type.');
  }

  private async computeChecksum(file: File): Promise<string> {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    return Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
  }

  private async logActivity(action: string, description: string, profileId?: string, documentId?: string) {
    if (!this.currentUser) return;
    try {
      await this.pbFetch('/api/collections/activity/records', {
        method: 'POST',
        body: JSON.stringify({ action, description, profile: profileId, document: documentId, actor: this.currentUser.id })
      });
    } catch { /* non-critical */ }
  }

  private mapReferenceBlock = (r: any): ReferenceBlock => ({ ...r, profileId: r.profile });
  private mapFinancialYear = (r: any): FinancialYear => ({ ...r, profileId: r.profile });
  private mapDocument = (r: any): Document => ({ ...r, profileId: r.profile, financialYearId: r.financialYear || undefined, currentVersionId: r.currentVersion || '' });
  private mapDocumentVersion = (r: any): DocumentVersion => ({ ...r, documentId: r.document });
  private mapRelationship = (r: any): Relationship => ({ ...r, fromProfileId: r.fromProfile, toProfileId: r.toProfile });
  private mapActivityRecord = (r: any): ActivityRecord => ({ ...r, profileId: r.profile || undefined, documentId: r.document || undefined, actor: r.actor || undefined });

  private levenshteinDistance(a: string, b: string): number {
    const m = a.length, n = b.length;
    if (m === 0) return n;
    if (n === 0) return m;
    const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0) as number[]);
    for (let i = 0; i <= m; i++) dp[i][0] = i;
    for (let j = 0; j <= n; j++) dp[0][j] = j;
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        dp[i][j] = a[i - 1] === b[j - 1] ? dp[i - 1][j - 1] : 1 + Math.min(dp[i - 1][j - 1], dp[i - 1][j], dp[i][j - 1]);
      }
    }
    return dp[m][n];
  }

  private normalizeFYQuery(q: string): string[] {
    const results: string[] = [];
    const fyMatch = q.match(/^(?:fy)?(\d{2,4})$/i);
    if (fyMatch) {
      let year = parseInt(fyMatch[1]);
      if (year < 100) year += 2000;
      results.push(String(year));
      results.push(`fy${year}`);
    }
    const slashMatch = q.match(/^(\d{2})\/(\d{2,4})$/);
    if (slashMatch) {
      let endYear = parseInt(slashMatch[2]);
      if (endYear < 100) endYear += 2000;
      results.push(String(endYear));
      results.push(`fy${endYear}`);
    }
    return results;
  }

  private expandSynonyms(q: string): string[] {
    const results: string[] = [];
    const syns = config.searchSynonyms;
    const ql = q.toLowerCase();
    if (syns[ql]) results.push(syns[ql].toLowerCase());
    for (const [key, value] of Object.entries(syns)) {
      if (key.includes(ql) && key !== ql) results.push(value.toLowerCase());
    }
    return results;
  }

  private computeMatchRank(term: string, fieldValue: string): number {
    const fv = fieldValue.toLowerCase();
    const t = term.toLowerCase();
    if (fv === t) return 100;
    if (fv.startsWith(t)) return 80;
    if (fv.split(/\s+/).some(token => token === t)) return 60;
    if (fv.includes(t)) return 30;
    if (t.length >= 4) {
      const tokens = fv.split(/\s+/);
      for (const token of tokens) {
        if (token.length >= 4 && this.levenshteinDistance(t, token) <= 1) return 20;
      }
    }
    return 0;
  }

  private describeMatch(term: string, fieldValue: string, fieldLabel: string): string {
    const fv = fieldValue.toLowerCase();
    const t = term.toLowerCase();
    if (fv === t) return `Exact ${fieldLabel} match`;
    if (fv.startsWith(t)) return `Prefix ${fieldLabel} match`;
    if (fv.split(/\s+/).some(token => token === t)) return `Token ${fieldLabel} match`;
    if (fv.includes(t)) return `Partial ${fieldLabel} match`;
    if (t.length >= 4) {
      const tokens = fv.split(/\s+/);
      for (const token of tokens) {
        if (token.length >= 4 && this.levenshteinDistance(t, token) <= 1) return `Near ${fieldLabel} match`;
      }
    }
    return `${fieldLabel} match`;
  }

  private htmlToPlainText(html: string): string {
    if (!html) return '';
    return html.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/\s+/g, ' ').trim();
  }

  async login(email: string, password: string): Promise<void> {
    const data = await this.pbFetch('/api/collections/users/auth-with-password', {
      method: 'POST',
      body: JSON.stringify({ identity: email, password })
    });
    this.saveAuthToStorage(data.token, data.record);
  }

  logout(): void {
    this.clearAuth();
  }

  getCurrentUser(): User | null {
    return this.currentUser;
  }

  async checkAuth(): Promise<boolean> {
    if (!this.token) return false;
    try {
      const data = await this.pbFetch('/api/collections/users/auth-refresh', { method: 'POST' });
      this.saveAuthToStorage(data.token, data.record);
      return true;
    } catch {
      this.clearAuth();
      return false;
    }
  }

  async requestPasswordReset(email: string): Promise<void> {
    await this.pbFetch('/api/collections/users/request-password-reset', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  }

  async confirmPasswordReset(token: string, password: string, passwordConfirm: string): Promise<void> {
    await this.pbFetch('/api/collections/users/confirm-password-reset', {
      method: 'POST',
      body: JSON.stringify({ token, password, passwordConfirm })
    });
  }

  async confirmVerification(token: string): Promise<void> {
    await this.pbFetch('/api/collections/users/confirm-verification', {
      method: 'POST',
      body: JSON.stringify({ token })
    });
  }

  async confirmEmailChange(token: string, password: string): Promise<void> {
    await this.pbFetch('/api/collections/users/confirm-email-change', {
      method: 'POST',
      body: JSON.stringify({ token, password })
    });
  }

  async listProfiles(): Promise<Profile[]> {
    const items = await this.pbFetchAll('profiles', '&sort=-created');
    return items;
  }

  async getProfile(id: string): Promise<Profile | null> {
    try {
      return await this.pbFetch(`/api/collections/profiles/records/${id}`);
    } catch (e: any) {
      if (e.status === 404) return null;
      throw e;
    }
  }

  async saveProfile(profile: Profile): Promise<Profile> {
    const isNew = !profile.id;
    const { id, created, updated, ...payload } = profile;
    const res = await this.pbFetch(isNew ? '/api/collections/profiles/records' : `/api/collections/profiles/records/${profile.id}`, {
      method: isNew ? 'POST' : 'PATCH',
      body: JSON.stringify(payload)
    });
    this.logActivity(isNew ? 'profile.created' : 'profile.updated', `Profile ${profile.displayName} ${isNew ? 'created' : 'updated'}`, res.id);
    return res;
  }

  async listReferenceBlocks(profileId: string): Promise<ReferenceBlock[]> {
    const filter = encodeURIComponent(`profile='${profileId}'`);
    const items = await this.pbFetchAll('profile_blocks', `&filter=${filter}&sort=-created`);
    return items.map(this.mapReferenceBlock);
  }

  async listAllReferenceBlocks(): Promise<ReferenceBlock[]> {
    const items = await this.pbFetchAll('profile_blocks', '&sort=-created');
    return items.map(this.mapReferenceBlock);
  }

  async saveReferenceBlock(block: ReferenceBlock): Promise<ReferenceBlock> {
    const isNew = !block.id;
    const payload = {
      blockType: block.blockType,
      label: block.label,
      fields: block.fields,
      archived: block.archived,
      profile: block.profileId
    };
    const res = await this.pbFetch(isNew ? '/api/collections/profile_blocks/records' : `/api/collections/profile_blocks/records/${block.id}`, {
      method: isNew ? 'POST' : 'PATCH',
      body: JSON.stringify(payload)
    });
    this.logActivity(isNew ? 'reference_block.created' : 'reference_block.updated', `${block.label} block updated`, block.profileId);
    return this.mapReferenceBlock(res);
  }

  async listFinancialYears(profileId?: string): Promise<FinancialYear[]> {
    const filterStr = profileId ? `&filter=${encodeURIComponent(`profile='${profileId}'`)}` : '';
    const items = await this.pbFetchAll('financial_years', `&sort=-endingYear${filterStr}`);
    return items.map(this.mapFinancialYear);
  }

  async getFinancialYear(id: string): Promise<FinancialYear | null> {
    try {
      const res = await this.pbFetch(`/api/collections/financial_years/records/${id}`);
      return this.mapFinancialYear(res);
    } catch (e: any) {
      if (e.status === 404) return null;
      throw e;
    }
  }

  async saveFinancialYear(year: FinancialYear): Promise<FinancialYear> {
    const isNew = !year.id;
    const payload = {
      endingYear: year.endingYear,
      status: year.status,
      grossIncome: year.grossIncome,
      taxableIncome: year.taxableIncome,
      taxWithheld: year.taxWithheld,
      taxRefund: year.taxRefund,
      taxPayable: year.taxPayable,
      capitalGainLoss: year.capitalGainLoss,
      carryForwardLoss: year.carryForwardLoss,
      superContributions: year.superContributions,
      accountantFee: year.accountantFee,
      lodgementDate: year.lodgementDate,
      assessmentDate: year.assessmentDate,
      annualSummary: year.annualSummary,
      archived: year.archived,
      profile: year.profileId
    };
    const res = await this.pbFetch(isNew ? '/api/collections/financial_years/records' : `/api/collections/financial_years/records/${year.id}`, {
      method: isNew ? 'POST' : 'PATCH',
      body: JSON.stringify(payload)
    });
    this.logActivity(isNew ? 'financial_year.created' : 'financial_year.updated', `FY${year.endingYear} updated`, year.profileId);
    return this.mapFinancialYear(res);
  }

  async listDocuments(filters?: DocumentFilter): Promise<Document[]> {
    const fStr: string[] = [];
    if (filters?.profileId) fStr.push(`profile='${filters.profileId}'`);
    if (filters?.financialYearId) fStr.push(`financialYear='${filters.financialYearId}'`);
    if (filters?.documentType) fStr.push(`documentType='${filters.documentType}'`);
    if (filters?.final !== undefined) fStr.push(`final=${filters.final}`);
    if (filters?.sensitive !== undefined) fStr.push(`sensitive=${filters.sensitive}`);
    if (filters?.archived !== undefined) fStr.push(`archived=${filters.archived}`);
    
    let extraQuery = '&sort=-updated';
    if (fStr.length > 0) extraQuery += `&filter=${encodeURIComponent(fStr.join(' && '))}`;
    
    const items = await this.pbFetchAll('documents', extraQuery);
    let docs = items.map(this.mapDocument) as Document[];
    
    if (filters?.search) {
      const q = filters.search.toLowerCase();
      docs = docs.filter(d => 
        d.title.toLowerCase().includes(q) || 
        d.originalFilename.toLowerCase().includes(q) || 
        d.documentType.toLowerCase().includes(q) || 
        (d.notes && !d.sensitive && d.notes.toLowerCase().includes(q)) || 
        (d.tags && d.tags.some(t => t.toLowerCase().includes(q)))
      );
    }
    return docs;
  }

  async getDocument(id: string): Promise<Document | null> {
    try {
      const res = await this.pbFetch(`/api/collections/documents/records/${id}`);
      return this.mapDocument(res);
    } catch (e: any) {
      if (e.status === 404) return null;
      throw e;
    }
  }

  async saveDocumentMetadata(doc: Document): Promise<Document> {
    return this.updateDocumentMetadata(doc.id, doc);
  }

  async updateDocumentMetadata(documentId: string, updates: Partial<Document>): Promise<Document> {
    const payload: any = { ...updates };
    if ('profileId' in payload) { payload.profile = payload.profileId; delete payload.profileId; }
    if ('financialYearId' in payload) { payload.financialYear = payload.financialYearId; delete payload.financialYearId; }
    if ('currentVersionId' in payload) { payload.currentVersion = payload.currentVersionId; delete payload.currentVersionId; }
    delete payload.id;
    delete payload.created;
    delete payload.updated;

    const res = await this.pbFetch(`/api/collections/documents/records/${documentId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload)
    });
    const mapped = this.mapDocument(res);
    this.logActivity('document.updated', `Metadata updated for ${mapped.title}`, mapped.profileId, mapped.id);
    return mapped;
  }

  async renameDocument(documentId: string, newTitle: string): Promise<Document> {
    const res = await this.pbFetch(`/api/collections/documents/records/${documentId}`, {
      method: 'PATCH',
      body: JSON.stringify({ title: newTitle })
    });
    const mapped = this.mapDocument(res);
    this.logActivity('document.renamed', `Document renamed to ${newTitle}`, mapped.profileId, mapped.id);
    return mapped;
  }

  async archiveDocument(documentId: string): Promise<Document> {
    const res = await this.pbFetch(`/api/collections/documents/records/${documentId}`, {
      method: 'PATCH',
      body: JSON.stringify({ archived: true })
    });
    const mapped = this.mapDocument(res);
    this.logActivity('document.archived', 'Document archived', mapped.profileId, mapped.id);
    return mapped;
  }

  async restoreDocument(documentId: string): Promise<Document> {
    const res = await this.pbFetch(`/api/collections/documents/records/${documentId}`, {
      method: 'PATCH',
      body: JSON.stringify({ archived: false })
    });
    const mapped = this.mapDocument(res);
    this.logActivity('document.restored', 'Document restored', mapped.profileId, mapped.id);
    return mapped;
  }

  async permanentlyDeleteDocument(documentId: string): Promise<void> {
    if (this.currentUser?.role !== 'owner') throw new Error('Only owners can permanently delete documents.');
    const doc = await this.getDocument(documentId);
    if (doc && !doc.archived) throw new Error('Document must be archived before permanent deletion.');
    
    const versions = await this.listDocumentVersions(documentId);
    for (const v of versions) {
      await this.pbFetch(`/api/collections/document_versions/records/${v.id}`, { method: 'DELETE' });
    }
    await this.pbFetch(`/api/collections/documents/records/${documentId}`, { method: 'DELETE' });
    this.logActivity('document.deleted', 'Vault record and files permanently deleted', '', documentId);
  }

  async stageDocuments(files: File[]): Promise<StagedDocument[]> {
    return files.map(file => ({
      id: `staged-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file,
      title: file.name.replace(/\.[^/.]+$/, ''),
      profileId: '',
      documentType: 'Receipt' as DocumentType,
      final: false,
      sensitive: false,
      status: 'staged' as const,
      checksum: ''
    }));
  }

  async uploadDocuments(batch: StagedDocument[]): Promise<StagedDocument[]> {
    const results: StagedDocument[] = [];
    const concurrency = config.upload.concurrency;
    
    for (let i = 0; i < batch.length; i += concurrency) {
      const chunk = batch.slice(i, i + concurrency);
      const chunkResults = await Promise.all(chunk.map(async (item) => {
        try {
          if (item.status === 'skipped') return item;
          item.status = 'uploading';
          if (!item.profileId) throw new Error('Profile is required');
          
          this.validateFileForUpload(item.file);
          const mimeType = this.normalizeMimeType(item.file);
          let checksum = item.checksum || '';
          if (!checksum) checksum = await this.computeChecksum(item.file);

          let docId: string;
          let versionNumber: number;

          if (item.duplicateAction === 'version' && item.duplicateDocumentId) {
            docId = item.duplicateDocumentId;
            const versions = await this.listDocumentVersions(docId);
            versionNumber = versions.length > 0 ? Math.max(...versions.map(v => v.versionNumber)) + 1 : 1;
          } else {
            const newDoc = await this.pbFetch('/api/collections/documents/records', {
              method: 'POST',
              body: JSON.stringify({
                title: item.title,
                originalFilename: item.file.name,
                profile: item.profileId,
                financialYear: item.financialYearId || '',
                documentType: item.documentType,
                notes: item.notes || '',
                tags: item.tags || [],
                final: item.final,
                sensitive: item.sensitive,
                archived: false,
                mimeType,
                fileSize: item.file.size
              })
            });
            docId = newDoc.id;
            versionNumber = 1;
          }

          const preparedFile = this.prepareFileForMultipart(item.file);
          const formData = new FormData();
          formData.append('document', docId);
          formData.append('versionNumber', String(versionNumber));
          formData.append('originalFilename', item.file.name);
          formData.append('mimeType', mimeType);
          formData.append('fileSize', String(item.file.size));
          formData.append('checksum', checksum);
          formData.append('final', String(item.final));
          formData.append('reason', item.notes?.trim() || 'Initial upload');
          formData.append('storageProvider', 'pocketbase');
          formData.append('storageStatus', 'uploaded');
          formData.append('uploadedBy', this.currentUser?.id || '');
          formData.append('protectedFile', preparedFile);

          let versionRecord;
          try {
            versionRecord = await this.pbFetch('/api/collections/document_versions/records', {
              method: 'POST',
              body: formData
            });
          } catch (e) {
            if (versionNumber === 1) {
              let cleanupFailed = false;
              try {
                await this.pbFetch(`/api/collections/documents/records/${docId}`, {
                  method: 'PATCH',
                  body: JSON.stringify({ archived: true })
                });
                await this.pbFetch(`/api/collections/documents/records/${docId}`, { method: 'DELETE' });
              } catch {
                cleanupFailed = true;
              }
              const baseMsg = e instanceof Error ? e.message : 'Version creation failed.';
              if (cleanupFailed) throw new Error(`${baseMsg} — An incomplete archived document record may remain.`);
              throw e;
            }
            throw e;
          }

          try {
            await this.pbFetch(`/api/collections/documents/records/${docId}`, {
              method: 'PATCH',
              body: JSON.stringify({ currentVersion: versionRecord.id, mimeType, fileSize: item.file.size })
            });
          } catch (e) {
            let versionCleanupFailed = false;
            try {
              await this.pbFetch(`/api/collections/document_versions/records/${versionRecord.id}`, { method: 'DELETE' });
            } catch {
              versionCleanupFailed = true;
            }
            if (versionNumber === 1) {
              let docCleanupFailed = false;
              try {
                await this.pbFetch(`/api/collections/documents/records/${docId}`, {
                  method: 'PATCH',
                  body: JSON.stringify({ archived: true })
                });
                await this.pbFetch(`/api/collections/documents/records/${docId}`, { method: 'DELETE' });
              } catch {
                docCleanupFailed = true;
              }
              item.status = 'failed';
              const baseMsg = e instanceof Error ? e.message : 'Metadata update failed.';
              item.error = docCleanupFailed ? `${baseMsg} — An incomplete archived document record may remain.` : `${baseMsg} Please retry.`;
              return item;
            }
            item.status = 'failed';
            const baseMsg = e instanceof Error ? e.message : 'Current-version update failed.';
            item.error = versionCleanupFailed ? `${baseMsg} The newly created version could not be removed.` : `${baseMsg} Please retry.`;
            return item;
          }

          this.logActivity('document.uploaded', `${item.file.name} uploaded`, item.profileId, docId);
          item.status = 'uploaded';
          return item;

        } catch (err) {
          item.status = 'failed';
          item.error = err instanceof Error ? err.message : 'Upload failed';
          return item;
        }
      }));
      results.push(...chunkResults);
    }
    return results;
  }

  private async getProtectedFileBlob(versionId: string, filename: string, download = false): Promise<Blob> {
    const tokenRes = await this.pbFetch('/api/files/token', { method: 'POST' });
    if (!tokenRes?.token) throw new Error('Failed to obtain file access token.');
    
    const fileToken = tokenRes.token;
    const url = `${this.pbUrl}/api/files/document_versions/${versionId}/${encodeURIComponent(filename)}?token=${fileToken}${download ? '&download=1' : ''}`;
    
    const headers: Record<string, string> = {};
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
    
    const res = await fetch(url, { headers });
    if (!res.ok) throw new Error('Failed to fetch protected file.');
    return res.blob();
  }

  async getDocumentBlob(documentId: string, versionId?: string): Promise<Blob | null> {
    const doc = await this.getDocument(documentId);
    if (!doc) return null;
    
    const targetVersionId = versionId || doc.currentVersionId;
    if (!targetVersionId) return null;
    
    const version = await this.pbFetch(`/api/collections/document_versions/records/${targetVersionId}`);
    if (!version?.protectedFile) throw new Error('File not found in storage.');
    
    return this.getProtectedFileBlob(version.id, version.protectedFile);
  }

  async downloadDocument(documentId: string, versionId?: string): Promise<{blob: Blob; filename: string}> {
    const doc = await this.getDocument(documentId);
    if (!doc) throw new Error('Document not found');
    
    const targetVersionId = versionId || doc.currentVersionId;
    const version = await this.pbFetch(`/api/collections/document_versions/records/${targetVersionId}`);
    if (!version?.protectedFile) throw new Error('File not found in storage.');
    
    const blob = await this.getProtectedFileBlob(version.id, version.protectedFile, true);
    return { blob, filename: version.originalFilename || doc.originalFilename };
  }

  async checkDocumentDuplicates(item: StagedDocument): Promise<DuplicateCheckResult> {
    if (!item.profileId) return { isDuplicate: false };

    if (item.checksum) {
      try {
        const checksumFilter = encodeURIComponent(`checksum='${item.checksum}'`);
        const versionMatches = await this.pbFetch(`/api/collections/document_versions/records?perPage=20&sort=-created&filter=${checksumFilter}`);
        for (const version of versionMatches.items || []) {
          if (!version.document) continue;
          try {
            const existing = await this.getDocument(version.document);
            if (!existing || existing.archived || existing.profileId !== item.profileId) continue;
            if (item.financialYearId && existing.financialYearId !== item.financialYearId) continue;
            return {
              isDuplicate: true,
              matchType: 'checksum',
              existingDocumentId: existing.id,
              existingDocumentTitle: existing.title
            };
          } catch {
            // Ignore inaccessible/stale version records and continue checking.
          }
        }
      } catch {
        // Checksum lookup is an enhancement only; preserve the established filename fallback.
      }
    }
    
    const safeName = item.file.name.replace(/'/g, "\\'");
    let filterStr = `originalFilename='${safeName}' && profile='${item.profileId}'`;
    if (item.financialYearId) filterStr += ` && financialYear='${item.financialYearId}'`;
    
    const filter = encodeURIComponent(filterStr);
    const res = await this.pbFetch(`/api/collections/documents/records?filter=${filter}`);
    
    if (res.items.length > 0) {
      return {
        isDuplicate: true,
        matchType: 'filename',
        existingDocumentId: res.items[0].id,
        existingDocumentTitle: res.items[0].title
      };
    }
    return { isDuplicate: false };
  }

  async listDocumentVersions(documentId: string): Promise<DocumentVersion[]> {
    const filter = encodeURIComponent(`document='${documentId}'`);
    const items = await this.pbFetchAll('document_versions', `&filter=${filter}&sort=-versionNumber`);
    return items.map(this.mapDocumentVersion);
  }

  async listAllDocumentVersions(): Promise<DocumentVersion[]> {
    const items = await this.pbFetchAll('document_versions', '&sort=-versionNumber');
    return items.map(this.mapDocumentVersion);
  }

  async addDocumentVersion(documentId: string, file: File, reason?: string): Promise<DocumentVersion> {
    const doc = await this.getDocument(documentId);
    if (!doc) throw new Error('Document not found');

    this.validateFileForUpload(file);
    const mimeType = this.normalizeMimeType(file);
    const checksum = await this.computeChecksum(file);

    const versions = await this.listDocumentVersions(documentId);
    const versionNumber = versions.length > 0 ? Math.max(...versions.map(v => v.versionNumber)) + 1 : 1;

    const preparedFile = this.prepareFileForMultipart(file);
    const formData = new FormData();
    formData.append('document', documentId);
    formData.append('versionNumber', String(versionNumber));
    formData.append('originalFilename', file.name);
    formData.append('mimeType', mimeType);
    formData.append('fileSize', String(file.size));
    formData.append('checksum', checksum);
    formData.append('final', String(doc.final));
    formData.append('reason', reason || 'New version uploaded');
    formData.append('storageProvider', 'pocketbase');
    formData.append('storageStatus', 'uploaded');
    formData.append('uploadedBy', this.currentUser?.id || '');
    formData.append('protectedFile', preparedFile);

    const versionRecord = await this.pbFetch('/api/collections/document_versions/records', {
      method: 'POST',
      body: formData
    });

    await this.pbFetch(`/api/collections/documents/records/${documentId}`, {
      method: 'PATCH',
      body: JSON.stringify({ currentVersion: versionRecord.id, mimeType, fileSize: file.size })
    });

    this.logActivity('document.version_added', `New version added to ${doc.title}`, doc.profileId, documentId);
    return this.mapDocumentVersion(versionRecord);
  }

  async listRelationships(profileId: string): Promise<Relationship[]> {
    const filter = encodeURIComponent(`fromProfile='${profileId}' || toProfile='${profileId}'`);
    const items = await this.pbFetchAll('relationships', `&filter=${filter}`);
    return items.map(this.mapRelationship);
  }

  async listAllRelationships(): Promise<Relationship[]> {
    const items = await this.pbFetchAll('relationships', '');
    return items.map(this.mapRelationship);
  }

  async listActivityRecords(profileId?: string): Promise<ActivityRecord[]> {
    const filterStr = profileId ? `&filter=${encodeURIComponent(`profile='${profileId}'`)}` : '';
    const items = await this.pbFetchAll('activity', `&sort=-created${filterStr}`);
    return items.map(this.mapActivityRecord);
  }

  async getDashboardSummary(): Promise<DashboardSummary> {
    const [profiles, years] = await Promise.all([this.listProfiles(), this.listFinancialYears()]);
    const activeProfiles = profiles.filter(profile => !profile.archived);
    const activeProfileIds = new Set(activeProfiles.map(profile => profile.id));
    const activeYears = years.filter(year => !year.archived && activeProfileIds.has(year.profileId));
    const filter = encodeURIComponent('archived=false');
    const docsRes = await this.pbFetch(`/api/collections/documents/records?sort=-created&perPage=500&filter=${filter}`);
    const docs: Document[] = docsRes.items.map(this.mapDocument).filter((doc: Document) => activeProfileIds.has(doc.profileId));
    
    const profileCards: ProfileCard[] = activeProfiles.map(profile => {
      const pYears = activeYears.filter(y => y.profileId === profile.id);
      const docCount = docs.filter(d => d.profileId === profile.id).length;
      return { profile, latestYear: pYears[0] ?? null, previousYear: pYears[1] ?? null, documentCount: docCount };
    });
    
    return { profileCards, recentDocuments: docs.slice(0, 5), recentFinancialYears: activeYears.slice(0, 5) };
  }

  async searchVault(query: string): Promise<SearchResult[]> {
    const q = query.toLowerCase().trim();
    if (!q) return [];
    
    const results: SearchResult[] = [];
    const seenIds = new Set<string>();
    const normalizedYears = this.normalizeFYQuery(q);
    const synonymTargets = this.expandSynonyms(q);
    const searchTerms = [q, ...normalizedYears, ...synonymTargets];
    
    for (const cmd of config.searchCommands) {
      const cmdLower = cmd.title.toLowerCase();
      let matched = false;
      let bestRank = 0;
      for (const term of searchTerms) {
        const r = this.computeMatchRank(term, cmdLower);
        if (r > 0) {
          matched = true;
          bestRank = Math.max(bestRank, r * 2);
        }
      }
      if (matched) {
        const id = `cmd-${cmd.title.toLowerCase().replace(/\s+/g, '-')}`;
        if (!seenIds.has(id)) {
          seenIds.add(id);
          results.push({
            id, resultType: 'command', title: cmd.title, subtitle: cmd.subtitle || 'Navigation',
            route: cmd.route, rank: bestRank, matchReason: 'Command match', sensitive: false
          });
        }
      }
    }

    try {
      const [profilesRes, docsRes, yearsRes, blocksRes, versionsRes] = await Promise.all([
        this.pbFetch('/api/collections/profiles/records?perPage=500&sort=-created'),
        this.pbFetch('/api/collections/documents/records?perPage=500&sort=-updated'),
        this.pbFetch('/api/collections/financial_years/records?perPage=500&sort=-endingYear'),
        this.pbFetch('/api/collections/profile_blocks/records?perPage=500&sort=-created'),
        this.pbFetch('/api/collections/document_versions/records?perPage=200&sort=-created')
      ]);

      for (const p of profilesRes.items) {
        const fields = [
          { value: p.displayName, weight: 10, label: 'Display name' },
          ...(p.legalName ? [{ value: p.legalName, weight: 8, label: 'Legal name' }] : []),
          { value: p.profileType, weight: 6, label: 'Profile type' },
          ...(p.aliases || []).map((a: string) => ({ value: a, weight: 7, label: 'Alias' }))
        ];
        let bestRank = 0;
        let bestReason = '';
        for (const field of fields) {
          if (!field.value) continue;
          for (const term of searchTerms) {
            const r = this.computeMatchRank(term, field.value);
            if (r > 0 && r * field.weight > bestRank) {
              bestRank = r * field.weight;
              bestReason = this.describeMatch(term, field.value, field.label);
            }
          }
        }
        if (bestRank > 0) {
          const id = `search-p-${p.id}`;
          if (!seenIds.has(id)) {
            seenIds.add(id);
            results.push({
              id, resultType: 'profile', title: p.displayName, subtitle: p.profileType + (p.legalName ? ` • ${p.legalName}` : ''),
              profileId: p.id, route: `/profiles/${p.id}`, rank: bestRank, matchReason: bestReason, sensitive: false, updated: p.updated
            });
          }
        }
      }

      for (const d of docsRes.items) {
        const mapped = this.mapDocument(d);
        const fields = [
          { value: mapped.title, weight: 10, label: 'Title', sensitive: false },
          { value: mapped.originalFilename, weight: 8, label: 'Filename', sensitive: false },
          { value: mapped.documentType, weight: 7, label: 'Type', sensitive: false },
          ...(mapped.sensitive ? [] : [
            { value: mapped.notes || '', weight: 4, label: 'Notes', sensitive: false },
            ...(mapped.tags || []).map(t => ({ value: t, weight: 5, label: 'Tag', sensitive: false }))
          ])
        ];
        let bestRank = 0;
        let bestReason = '';
        for (const field of fields) {
          if (!field.value) continue;
          for (const term of searchTerms) {
            const r = this.computeMatchRank(term, field.value);
            if (r > 0 && r * field.weight > bestRank) {
              bestRank = r * field.weight;
              bestReason = this.describeMatch(term, field.value, field.label);
            }
          }
        }
        if (bestRank > 0) {
          const id = `search-d-${mapped.id}`;
          if (!seenIds.has(id)) {
            seenIds.add(id);
            results.push({
              id, resultType: 'document', title: mapped.title, subtitle: mapped.documentType + (mapped.sensitive ? ' • Sensitive' : ''),
              profileId: mapped.profileId, documentId: mapped.id, rank: bestRank, matchReason: bestReason, sensitive: mapped.sensitive, updated: mapped.updated
            });
          }
        }
      }

      for (const y of yearsRes.items) {
        const mapped = this.mapFinancialYear(y);
        const yearStr = String(mapped.endingYear);
        const fyStr = `FY${mapped.endingYear}`;
        const summaryText = this.htmlToPlainText(mapped.annualSummary || '');
        const fields = [
          { value: fyStr, weight: 10, label: 'Financial year' },
          ...(normalizedYears.length > 0 ? [{ value: yearStr, weight: 9, label: 'Year' }] : []),
          { value: mapped.status, weight: 6, label: 'Status' },
          ...(summaryText ? [{ value: summaryText, weight: 3, label: 'Summary' }] : [])
        ];
        let bestRank = 0;
        let bestReason = '';
        for (const field of fields) {
          if (!field.value) continue;
          for (const term of searchTerms) {
            const r = this.computeMatchRank(term, field.value);
            if (r > 0 && r * field.weight > bestRank) {
              bestRank = r * field.weight;
              bestReason = this.describeMatch(term, field.value, field.label);
            }
          }
        }
        if (bestRank > 0) {
          const id = `search-fy-${mapped.id}`;
          if (!seenIds.has(id)) {
            seenIds.add(id);
            results.push({
              id, resultType: 'financial_year', title: fyStr, subtitle: mapped.status,
              profileId: mapped.profileId, financialYearId: mapped.id, route: `/profiles/${mapped.profileId}/financial-years/${mapped.id}`,
              rank: bestRank, matchReason: bestReason, sensitive: false, updated: mapped.updated
            });
          }
        }
      }

      for (const b of blocksRes.items) {
        const mapped = this.mapReferenceBlock(b);
        const nonSensitiveFields = mapped.fields.filter(f => !f.sensitive);
        const fields = [
          { value: mapped.label, weight: 8, label: 'Block label' },
          ...(mapped.blockType !== mapped.label ? [{ value: mapped.blockType, weight: 6, label: 'Block type' }] : []),
          ...nonSensitiveFields.map(f => ({ value: f.value, weight: 5, label: `Field: ${f.label}` })),
          ...nonSensitiveFields.map(f => ({ value: f.label, weight: 4, label: 'Field label' }))
        ];
        let bestRank = 0;
        let bestReason = '';
        for (const field of fields) {
          if (!field.value) continue;
          for (const term of searchTerms) {
            const r = this.computeMatchRank(term, field.value);
            if (r > 0 && r * field.weight > bestRank) {
              bestRank = r * field.weight;
              bestReason = this.describeMatch(term, field.value, field.label);
            }
          }
        }
        if (bestRank > 0) {
          const id = `search-rb-${mapped.id}`;
          if (!seenIds.has(id)) {
            seenIds.add(id);
            results.push({
              id, resultType: 'reference_block', title: mapped.label, subtitle: mapped.blockType,
              profileId: mapped.profileId, route: `/profiles/${mapped.profileId}`, rank: bestRank, matchReason: bestReason, sensitive: false, updated: mapped.updated
            });
          }
        }
      }

      for (const v of versionsRes.items) {
        const mapped = this.mapDocumentVersion(v);
        const fields = [
          { value: mapped.originalFilename, weight: 6, label: 'Filename' },
          ...(mapped.reason ? [{ value: mapped.reason, weight: 5, label: 'Reason' }] : []),
          { value: `Version ${mapped.versionNumber}`, weight: 4, label: 'Version' }
        ];
        let bestRank = 0;
        let bestReason = '';
        for (const field of fields) {
          if (!field.value) continue;
          for (const term of searchTerms) {
            const r = this.computeMatchRank(term, field.value);
            if (r > 0 && r * field.weight > bestRank) {
              bestRank = r * field.weight;
              bestReason = this.describeMatch(term, field.value, field.label);
            }
          }
        }
        if (bestRank > 0) {
          const id = `search-v-${mapped.id}`;
          if (!seenIds.has(id)) {
            seenIds.add(id);
            results.push({
              id, resultType: 'document_version', title: `V${mapped.versionNumber}: ${mapped.originalFilename}`, subtitle: mapped.reason || 'Version',
              documentId: mapped.documentId, versionId: mapped.id, rank: bestRank, matchReason: bestReason, sensitive: false, updated: mapped.created
            });
          }
        }
      }

    } catch (err) {
      throw err;
    }

    results.sort((a, b) => b.rank - a.rank);
    return results.slice(0, 50);
  }
}